import {
    adminHasMenuView,
    adminHasFoodOrdersView,
    adminHasReservationsView,
    adminHasProductsView,
    adminHasProductOrdersView,
    adminHasPropertiesView,
    adminHasCarsView,
    adminHasTeamView,
} from '../Utils/adminPermissions.js';
import { isBillingInactive } from '../Utils/billingPaymentRequired.js';

export const STORE_MODE_IDS = {
    RESTAURANT: 'restaurant',
    RETAIL: 'retail',
    REAL_ESTATE: 'realEstate',
    AUTOMOTIVE: 'automotive',
    HRMS: 'hrms',
};

export const STORE_MODES = [
    {
        id: STORE_MODE_IDS.RESTAURANT,
        label: 'Restaurant / Cafe / Bar',
        description: 'Run your restaurant, cafe, or bar — menu, food & drink orders, table bookings, and delivery.',
        homeSubtitle: "Here's what's happening with your venue today.",
        hasAccess: (user) =>
            adminHasMenuView(user) ||
            adminHasFoodOrdersView(user) ||
            adminHasReservationsView(user),
    },
    {
        id: STORE_MODE_IDS.RETAIL,
        label: 'Shop & Products',
        description: 'Sell items online — manage products, stock, and customer orders.',
        homeSubtitle: 'Track products, orders, and customer activity.',
        hasAccess: (user) =>
            adminHasProductsView(user) || adminHasProductOrdersView(user),
    },
    {
        id: STORE_MODE_IDS.REAL_ESTATE,
        label: 'Real Estate',
        description: 'Buy, rent, off-plan, seller leads, services, and agents.',
        homeSubtitle: 'Track listings, seller leads, services, and agents.',
        hasAccess: (user) => adminHasPropertiesView(user),
    },
    {
        id: STORE_MODE_IDS.AUTOMOTIVE,
        label: 'Automotive',
        description: 'Vehicles, services, trade-ins, appointments, and advisors.',
        homeSubtitle: 'Track listings, services, appointments, and customer activity.',
        hasAccess: (user) => adminHasCarsView(user),
    },
    {
        id: STORE_MODE_IDS.HRMS,
        label: 'Staff & HR',
        description: 'Manage employees, daily attendance, and time-off requests.',
        homeSubtitle: 'Headcount, attendance, and leave requests at a glance.',
        hasAccess: (user) => adminHasTeamView(user),
    },
];

const LIBRARY_VISIBILITY = {
    [STORE_MODE_IDS.RESTAURANT]: new Set([
        'menu',
        'foodOrders',
        'coupons',
        'appUsers',
    ]),
    [STORE_MODE_IDS.RETAIL]: new Set([
        'products',
        'productOrders',
        'coupons',
        'appUsers',
    ]),
    [STORE_MODE_IDS.REAL_ESTATE]: new Set(['properties', 'appUsers']),
    [STORE_MODE_IDS.AUTOMOTIVE]: new Set(['cars', 'appUsers']),
};

const CUSTOMER_VISIBILITY = {
    [STORE_MODE_IDS.RESTAURANT]: new Set([
        'contacts',
        'reservations',
        'subscribers',
        'newsletter',
    ]),
    [STORE_MODE_IDS.RETAIL]: new Set(['contacts', 'subscribers', 'newsletter']),
    [STORE_MODE_IDS.REAL_ESTATE]: new Set(['contacts', 'subscribers', 'newsletter']),
    [STORE_MODE_IDS.AUTOMOTIVE]: new Set(['contacts', 'subscribers', 'newsletter']),
};

const SETTINGS_VISIBILITY = {
    [STORE_MODE_IDS.RESTAURANT]: new Set(['general', 'openHours', 'contentBlocks', 'manageStore', 'storeDomains']),
    [STORE_MODE_IDS.RETAIL]: new Set(['general', 'contentBlocks', 'manageStore', 'storeDomains']),
    [STORE_MODE_IDS.REAL_ESTATE]: new Set(['general', 'contentBlocks', 'manageStore', 'storeDomains']),
    [STORE_MODE_IDS.AUTOMOTIVE]: new Set(['general', 'contentBlocks', 'manageStore', 'storeDomains']),
    [STORE_MODE_IDS.RESTAURANT]: new Set(['general', 'openHours', 'contentBlocks', 'manageStore', 'storeDomains', 'storefrontPages', 'storeQr', 'storePayments']),
    [STORE_MODE_IDS.RETAIL]: new Set(['general', 'contentBlocks', 'manageStore', 'storeDomains', 'storefrontPages', 'storeQr', 'storePayments']),
    [STORE_MODE_IDS.REAL_ESTATE]: new Set(['general', 'contentBlocks', 'manageStore', 'storeDomains', 'storefrontPages', 'storeQr']),
    [STORE_MODE_IDS.AUTOMOTIVE]: new Set(['general', 'contentBlocks', 'manageStore', 'storeDomains', 'storefrontPages', 'storeQr']),
};

/**
 * General-settings tabs that only apply to some store modes. Tabs left out of
 * this map are shown for every mode.
 *
 * `order-types` drives pickup / delivery / dine-in, which only food orders use —
 * retail orders ship, and real-estate and automotive stores take no orders.
 */
const GENERAL_SETTINGS_TAB_VISIBILITY = {
    'order-types': new Set([STORE_MODE_IDS.RESTAURANT]),
};

const ROUTE_PREFIXES_BY_MODE = {
    [STORE_MODE_IDS.RESTAURANT]: [
        '/menu',
        '/food-orders',
        '/todays-food-orders',
        '/food-order',
        '/reservations',
        '/coupons',
        '/store-payments',
    ],
    [STORE_MODE_IDS.RETAIL]: [
        '/items',
        '/products',
        '/add-new-item',
        '/edit-item',
        '/product-orders',
        '/todays-product-orders',
        '/product-order',
        '/coupons',
        '/store-payments',
    ],
    [STORE_MODE_IDS.REAL_ESTATE]: ['/properties'],
    [STORE_MODE_IDS.AUTOMOTIVE]: ['/cars'],
    [STORE_MODE_IDS.HRMS]: ['/team'],
};

const SHARED_ROUTE_PREFIXES = [
    '/home',
    '/',
    '/notifications',
    '/chat',
    '/support-inbox',
    '/analytics',
    '/login-security',
    '/admins-managements',
    '/account-setting',
    '/legal',
    '/password-required',
    '/select-store',
    '/select-plan',
    '/users',
    '/content-blocks',
    '/contact-messages',
    '/subscribers',
    '/general-settings',
    '/open-hours',
    '/manage-store',
    '/store-domains',
    '/store-qr',
    '/online-store',
    '/storefront-pages',
    '/coupons',
];

export function getAvailableStoreModes(user) {
    if (!user) return [];
    return STORE_MODES.filter((mode) => mode.hasAccess(user));
}

export function getStoreModeLabel(modeId, t) {
    if (!modeId) return '';
    if (typeof t === 'function') {
        const key = `selectStore.modes.${modeId}`;
        const translated = t(key);
        if (translated !== key) return translated;
    }
    return STORE_MODES.find((mode) => mode.id === modeId)?.label ?? modeId;
}

export function getStoreModeHomeGreeting(modeId, t) {
    const mode = STORE_MODES.find((entry) => entry.id === modeId);
    if (!mode) {
        return {
            eyebrow: typeof t === 'function' ? t('common.dashboard') : 'Dashboard',
            subtitle:
                typeof t === 'function'
                    ? t('storeModes.defaultSubtitle')
                    : "Here's what's happening today.",
        };
    }
    const eyebrow = getStoreModeLabel(modeId, t);
    const subtitle =
        typeof t === 'function'
            ? t(`storeModes.${modeId}.subtitle`)
            : (mode.homeSubtitle ?? mode.description);
    return {
        eyebrow,
        subtitle: subtitle === `storeModes.${modeId}.subtitle`
            ? (mode.homeSubtitle ?? mode.description)
            : subtitle,
    };
}

export function libraryItemVisibleInStoreMode(itemId, storeMode) {
    if (!storeMode) return true;
    if (storeMode === STORE_MODE_IDS.HRMS) return false;
    return LIBRARY_VISIBILITY[storeMode]?.has(itemId) ?? false;
}

export function customerItemVisibleInStoreMode(itemId, storeMode) {
    if (!storeMode) return true;
    if (storeMode === STORE_MODE_IDS.HRMS) return false;
    return CUSTOMER_VISIBILITY[storeMode]?.has(itemId) ?? false;
}

export function settingsItemVisibleInStoreMode(itemId, storeMode) {
    if (!storeMode) return true;
    if (storeMode === STORE_MODE_IDS.HRMS) return false;
    return SETTINGS_VISIBILITY[storeMode]?.has(itemId) ?? false;
}

export function generalSettingsTabVisibleInStoreMode(tabId, storeMode) {
    const modes = GENERAL_SETTINGS_TAB_VISIBILITY[tabId];
    if (!modes) return true;
    // Store mode is still resolving — don't hide a tab we may need to show.
    if (!storeMode) return true;
    return modes.has(storeMode);
}

export function teamSectionVisibleInStoreMode(storeMode) {
    if (!storeMode) return true;
    return storeMode === STORE_MODE_IDS.HRMS;
}

export function librarySectionVisibleInStoreMode(storeMode) {
    if (!storeMode) return true;
    return storeMode !== STORE_MODE_IDS.HRMS;
}

export function customerSectionVisibleInStoreMode(storeMode) {
    if (!storeMode) return true;
    return storeMode !== STORE_MODE_IDS.HRMS;
}

function pathMatchesPrefix(pathname, prefix) {
    return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

export function routeAllowedInStoreMode(pathname, storeMode) {
    if (!storeMode) return true;

    if (storeMode !== STORE_MODE_IDS.HRMS && pathMatchesPrefix(pathname, '/team')) {
        return false;
    }

    if (storeMode === STORE_MODE_IDS.HRMS) {
        const hrmsBlockedPrefixes = [
            '/users',
            '/content-blocks',
            '/contact-messages',
            '/subscribers',
            '/coupons',
            '/general-settings',
            '/open-hours',
            '/store-domains',
            '/store-qr',
            '/online-store',
            '/menu',
            '/food-orders',
            '/reservations',
            '/items',
            '/products',
            '/product-orders',
            '/properties',
            '/cars',
        ];
        if (hrmsBlockedPrefixes.some((prefix) => pathMatchesPrefix(pathname, prefix))) {
            return false;
        }
    }

    if (SHARED_ROUTE_PREFIXES.some((prefix) => pathMatchesPrefix(pathname, prefix))) {
        if (pathname === '/coupons' || pathname.startsWith('/coupons/')) {
            return (
                storeMode === STORE_MODE_IDS.RESTAURANT ||
                storeMode === STORE_MODE_IDS.RETAIL
            );
        }
        if (pathname === '/open-hours' || pathname.startsWith('/open-hours/')) {
            return storeMode === STORE_MODE_IDS.RESTAURANT;
        }
        if (pathname === '/store-domains' || pathname.startsWith('/store-domains/')) {
            return (
                storeMode === STORE_MODE_IDS.RESTAURANT ||
                storeMode === STORE_MODE_IDS.RETAIL ||
                storeMode === STORE_MODE_IDS.REAL_ESTATE ||
                storeMode === STORE_MODE_IDS.AUTOMOTIVE
            );
        }
        if (pathname === '/store-qr' || pathname.startsWith('/store-qr/')) {
            return (
                storeMode === STORE_MODE_IDS.RESTAURANT ||
                storeMode === STORE_MODE_IDS.RETAIL ||
                storeMode === STORE_MODE_IDS.REAL_ESTATE ||
                storeMode === STORE_MODE_IDS.AUTOMOTIVE
            );
        }
        if (pathname === '/reservations' || pathname.startsWith('/reservations/')) {
            return storeMode === STORE_MODE_IDS.RESTAURANT;
        }
        return true;
    }

    const allowedPrefixes = ROUTE_PREFIXES_BY_MODE[storeMode] || [];
    return allowedPrefixes.some((prefix) => pathMatchesPrefix(pathname, prefix));
}

export function getPostLoginPath({ passwordChangeRequired, storesCount, billingStatus }) {
    if (passwordChangeRequired) return '/password-required';

    // Expired trial / canceled / past_due: send to billing or store pick — never create-store.
    if (isBillingInactive(billingStatus)) {
        if (storesCount > 1) return '/select-store';
        return '/account-setting?tab=billing';
    }

    if (storesCount === 0) return '/select-store?create=1';
    if (storesCount > 1) return '/select-store';
    return '/home';
}

